import { useQuery } from '@apollo/client/react';
import clsx from 'clsx';
import { Trans, useTranslation } from 'next-i18next/pages';
import Link from 'next/link';
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { JobBoardFiltersInput } from '@pflegia/data-access';
import { PaginationSortField } from '@pflegia/types/common/job-position/pagination-sort-field';
import { ConnectionInfo } from '@pflegia/types/common/match/dialog';
import { useAuth } from '@pflegia/ui/contexts/auth.context';
import { useStrippedRouter } from '@pflegia/ui/hooks/use-stripped-router';
import { ReactComponent as ArrowBack } from '@pflegia/ui/icons/arrow-back.svg';
import { JOB, JOB_BOARD, JOB_DETAILS, JOBS } from '@pflegia/ui/pflegia/constants/routes';
import { useMatchDialogueStore } from '@pflegia/ui/stores/match-dialogue.store';
import { normalizeJobsParams } from '@pflegia/util/common/normalize-jobs-params';

import SlimPositionCard from '@components/slim-position-card/SlimPositionCard';

import { GET_JOB_POSITIONS_LIST } from '@shared/graphql/operations/job-positions-list';
import { GET_NOT_LOGGED_JOB_POSITIONS_LIST } from '@shared/graphql/operations/not-logged-job-positions-list';
import { getMagazinUtmRecord, MAGAZIN_UTM_CONTENT, MagazinAttribution } from '@shared/utils/magazin';

const JOBS_PER_PAGE = 9;
const SKELETON_COUNT = 3;
const SCROLL_TOLERANCE = 16;
const CARD_WIDTH_CLASS = 'w-full shrink-0 snap-start sm:w-[calc((100%-1.25rem)/2)] xl:w-[calc((100%-2.5rem)/3)]';
// 3-up gated on container width (@5xl≈64rem), not viewport: the article column is narrow, the magazin index is full-width.
const CARD_WIDTH_CLASS_COMPACT = 'w-full shrink-0 snap-start sm:w-[calc((100%-1.25rem)/2)] @5xl:w-[calc((100%-2.5rem)/3)]';
const CARD_SURFACE_CLASS = 'min-h-[16rem] border-primary-100 shadow-none drop-shadow-none sm:min-h-[18rem] md:min-h-[20rem]';

interface SalaryCalculatorJobsCarouselProps {
  city?: string;
  filters?: Partial<JobBoardFiltersInput>;
  isCompact?: boolean;
  position?: string;
  title?: ReactNode;
  magazinAttribution?: MagazinAttribution;
}

interface CarouselNavigationButtonProps {
  isCompact?: boolean;
  direction: 'previous' | 'next';
  disabled: boolean;
  label: string;
  onClick: () => void;
}

const SalaryCalculatorJobsCarousel: React.FC<SalaryCalculatorJobsCarouselProps> = ({
  city,
  filters,
  isCompact = false,
  position,
  title,
  magazinAttribution,
}) => {
  const { t } = useTranslation('gehaltsrechner');
  const isAuthenticated = useAuth();
  const openMatchDialog = useMatchDialogueStore(state => state.onOpen);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollPrevious, setCanScrollPrevious] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const { isBlogPage } = useStrippedRouter();

  const normalizedFilters = useMemo<JobBoardFiltersInput>(
    () => ({
      departmentIds: filters?.departmentIds ?? [],
      employmentTypes: filters?.employmentTypes ?? [],
      jobPositionTitleQueries: filters?.jobPositionTitleQueries ?? [],
      jobTitleIds: filters?.jobTitleIds,
      locations: filters?.locations,
      permanentOrTemporary: filters?.permanentOrTemporary,
      typesOfWork: filters?.typesOfWork ?? [],
      ...(filters?.jobTitleIds?.length ? {} : { excludePriorities: filters?.excludePriorities }),
    }),
    [filters],
  );
  const authenticatedJobsQuery = useQuery(GET_JOB_POSITIONS_LIST, {
    fetchPolicy: 'network-only',
    skip: !isAuthenticated,
    variables: {
      page: 0,
      perPage: JOBS_PER_PAGE,
      sortField: PaginationSortField.Salary,
      sortOrder: 'DESC',
      filter: {
        omitExistingMatches: true,
        ...(normalizedFilters.jobTitleIds?.length ? { jobTitleIds: normalizedFilters.jobTitleIds } : {}),
        ...(normalizedFilters.locations?.length ? { locations: normalizedFilters.locations } : {}),
        ...(normalizedFilters.excludePriorities?.length ? { excludePriorities: normalizedFilters.excludePriorities } : {}),
      },
    },
  });
  const guestJobsQuery = useQuery(GET_NOT_LOGGED_JOB_POSITIONS_LIST, {
    fetchPolicy: 'cache-and-network',
    skip: isAuthenticated,
    variables: {
      page: 0,
      perPage: JOBS_PER_PAGE,
      sortField: PaginationSortField.Salary,
      sortOrder: 'DESC',
      filters: normalizedFilters,
    },
  });
  const { data, loading } = isAuthenticated ? authenticatedJobsQuery : guestJobsQuery;

  const jobs = useMemo(() => data?.data?.jobPositions ?? [], [data?.data?.jobPositions]);
  const jobDetailsBasePath = isAuthenticated ? JOB : JOB_DETAILS;
  const jobsPath = useMemo(() => {
    const basePath = isAuthenticated ? JOB_BOARD : JOBS;
    const pathSegments = [position, city].filter(Boolean).map(value => normalizeJobsParams(value as string));
    const path = pathSegments.length ? `${basePath}/${pathSegments.join('/')}` : basePath;

    if (!magazinAttribution) return path;

    const utmHash = new URLSearchParams(getMagazinUtmRecord(magazinAttribution, MAGAZIN_UTM_CONTENT.currentJobsAllJobs)).toString();
    return `${path}#${utmHash}`;
  }, [city, isAuthenticated, position, magazinAttribution]);
  const openDialog = useCallback((connectionInfo: ConnectionInfo) => openMatchDialog(connectionInfo), [openMatchDialog]);

  const updateScrollState = useCallback(() => {
    const container = scrollRef.current;

    if (!container) return;

    const maxScrollLeft = container.scrollWidth - container.clientWidth;
    setCanScrollPrevious(container.scrollLeft > SCROLL_TOLERANCE);
    setCanScrollNext(maxScrollLeft - container.scrollLeft > SCROLL_TOLERANCE);
  }, []);

  const handleScroll = useCallback((direction: 'previous' | 'next') => {
    const container = scrollRef.current;

    if (!container) return;

    container.scrollBy({
      // eslint-disable-next-line no-magic-numbers
      left: direction === 'next' ? container.clientWidth * 0.9 : container.clientWidth * -0.9,
      behavior: 'smooth',
    });
  }, []);

  useEffect(() => {
    updateScrollState();
  }, [jobs.length, updateScrollState]);

  useEffect(() => {
    const container = scrollRef.current;

    if (!container) return;

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateScrollState);

      return () => window.removeEventListener('resize', updateScrollState);
    }

    const resizeObserver = new ResizeObserver(updateScrollState);
    resizeObserver.observe(container);

    return () => resizeObserver.disconnect();
  }, [updateScrollState]);

  const showNavigation = jobs.length > SKELETON_COUNT;
  const renderedTitle = title ?? (
    <h2 className={clsx(isCompact ? 'my-0! text-start text-xl font-medium md:ml-6 md:text-2xl' : 'text-center text-2xl md:text-4xl')}>
      <Trans
        components={{
          span: <span className={clsx(isCompact ? 'text-black' : 'font-handwriting text-main-blue align-middle md:text-5xl')} />,
        }}
        i18nKey='jobsSection.title'
        t={t}
      />
    </h2>
  );

  if (!loading && jobs.length === 0) return null;

  return (
    <div>
      {renderedTitle}

      <div
        className={clsx(
          'mt-10 grid gap-4 md:mt-14 md:grid-cols-[3.5rem_minmax(0,1fr)_3.5rem] md:items-center lg:gap-6',
          isCompact && 'relative mt-6! md:mt-4! md:grid-cols-none md:px-6',
        )}>
        <div className={clsx('hidden justify-center md:flex', isCompact && 'absolute top-1/2 left-6 -translate-y-1/2')}>
          {showNavigation ? (
            <CarouselNavigationButton
              isCompact={isCompact}
              direction='previous'
              disabled={!canScrollPrevious}
              label={t('jobsSection.previous')}
              onClick={() => handleScroll('previous')}
            />
          ) : (
            <div className='h-14 w-14' />
          )}
        </div>

        <div
          className='scrollbar-hide @container flex snap-x snap-mandatory gap-5 overflow-x-auto py-4'
          onScroll={updateScrollState}
          ref={scrollRef}>
          {loading &&
            Array.from({ length: SKELETON_COUNT }, (_, index) => (
              <SalaryCalculatorJobCardSkeleton isBlogPage={isBlogPage} key={`salary-calculator-job-card-skeleton-${index}`} />
            ))}

          {!loading &&
            jobs.map(job => (
              <div className={isBlogPage ? CARD_WIDTH_CLASS_COMPACT : CARD_WIDTH_CLASS} key={job.id}>
                <SlimPositionCard
                  basePathOverride={jobDetailsBasePath}
                  cardClassName={CARD_SURFACE_CLASS}
                  isLinkLoadingDisabled
                  headingsClassName='text-xl line-clamp-4 md:text-xl'
                  employerClassName='text-xl line-clamp-4 md:text-xl'
                  compact
                  jobPosition={job.jobPosition}
                  openDialog={openDialog}
                  refetchQueries={isAuthenticated ? [GET_JOB_POSITIONS_LIST] : undefined}
                  positionLinkTarget='_blank'
                  magazinAttribution={magazinAttribution}
                />
              </div>
            ))}

          {!loading && jobs.length === 0 && (
            <div className='border-primary-100 text-default w-full rounded-2xl border bg-white px-6 py-12 text-center text-lg'>
              {t('jobsSection.empty')}
            </div>
          )}
        </div>

        <div className={clsx('hidden justify-center md:flex', isCompact && 'absolute top-1/2 right-6 -translate-y-1/2')}>
          {showNavigation ? (
            <CarouselNavigationButton
              isCompact={isCompact}
              direction='next'
              disabled={!canScrollNext}
              label={t('jobsSection.next')}
              onClick={() => handleScroll('next')}
            />
          ) : (
            <div className='h-14 w-14' />
          )}
        </div>
      </div>

      <div className={clsx('mt-2 mr-0 flex justify-end md:mt-6 md:mr-20', isCompact && 'md:mr-6!')}>
        <Link
          className='text-primary inline-flex items-center gap-3 text-base font-medium no-underline! transition-transform hover:translate-x-1 hover:underline'
          href={jobsPath}
          rel='noopener noreferrer'
          target='_blank'>
          <span>{t('jobsSection.viewMore')}</span>
          <ArrowBack className='h-6 w-6 rotate-180' />
        </Link>
      </div>
    </div>
  );
};

const SalaryCalculatorJobCardSkeleton: React.FC<{ isBlogPage: boolean }> = ({ isBlogPage }) => (
  <div
    className={clsx(
      isBlogPage ? CARD_WIDTH_CLASS_COMPACT : CARD_WIDTH_CLASS,
      'border-primary-100 flex flex-col rounded-2xl border bg-white p-4 md:p-6',
      CARD_SURFACE_CLASS,
    )}>
    <div className='animate-pulse'>
      <div className='flex grow justify-between gap-x-2'>
        <div className='flex flex-1 flex-col gap-y-2'>
          <div className='h-5 w-11/12 rounded-full bg-slate-200 md:h-7' />
          <div className='h-5 w-8/12 rounded-full bg-slate-200 md:h-7' />
          <div className='h-5 w-7/12 rounded-full bg-slate-200 md:h-7' />
          <div className='mt-4 h-4 w-9/12 rounded-full bg-slate-200 md:mt-5 md:h-6' />
        </div>
        <div className='h-12 w-12 shrink-0 rounded-lg bg-slate-200 md:h-20 md:w-20' />
      </div>

      <div className='mt-20 flex flex-col justify-between gap-y-6'>
        <div className='flex flex-col gap-y-2'>
          <div className='h-4 w-5/12 rounded-full bg-slate-200 md:h-5' />
          <div className='h-4 w-4/12 rounded-full bg-slate-200 md:h-5' />
        </div>

        <div className='flex flex-col gap-3 sm:flex-row'>
          <div className='h-14 w-full rounded-full bg-slate-200 md:h-16' />
          <div className='h-14 w-full rounded-full bg-slate-200 md:h-16' />
        </div>
      </div>
    </div>
  </div>
);

const CarouselNavigationButton: React.FC<CarouselNavigationButtonProps> = ({ direction, disabled, label, isCompact = false, onClick }) => (
  <button
    aria-label={label}
    className={clsx(
      'hidden items-center justify-center rounded-full transition-all md:inline-flex',
      isCompact ? '-mt-1 h-12 w-12' : 'h-14 w-14',
      disabled && 'bg-dot-inactive/30 text-dot-inactive cursor-default shadow-none',
      !disabled && direction === 'next'
        ? 'bg-primary text-white shadow-lg hover:shadow-xl'
        : !disabled && 'text-primary hover:text-default bg-white shadow-lg',
    )}
    disabled={disabled}
    onClick={onClick}
    type='button'>
    <ArrowBack className={clsx(isCompact ? 'h-5 w-5' : 'h-6 w-6', direction === 'next' && 'rotate-180')} />
  </button>
);

export default SalaryCalculatorJobsCarousel;
